import { useEffect } from 'react';
import {
  Icon,
  Text,
  Box,
  Heading,
  AspectRatio,
  Image,
  VStack,
  HStack,
  Link,
  StackDivider,
  Spinner,
  Center,
  useToast,
} from '@chakra-ui/react';

import PageContainer from '~/containers/PageContainer';
import Content from '~/containers/Content';

import LocationBar from '~/components/LocationBar';
import BackwardBar from '~/components/BackwardBar';
import SectionLabel from '~/components/SectionLabel';
import OperatingTimeList from '~/components/OperatingTimeList';

import { IoCallOutline, IoLocationOutline, IoTimeOutline } from 'react-icons/io5';
import { AiOutlineShop } from 'react-icons/ai';

import { useNavigate, useParams } from 'react-router-dom';
import usePickMealPoint from '~/pages/PickMealLocationInfo/hooks/usePickMealPoint';

import * as fp from 'lodash/fp';

import tzuchiDefaultImg from '~/assets/images/defaultImage.png';

import { FirebaseDataStatus } from '~/types/utilities';

export default function PickMealLocationInfo() {
  const toast = useToast();
  const { pointId } = useParams();
  const { remotePickMealPoint, dataStatus } = usePickMealPoint(pointId || '');
  const navigate = useNavigate();

  useEffect(() => {
    if (fp.eq(dataStatus, FirebaseDataStatus.NOT_EXIST)) {
      toast({
        title: '找不到取餐點',
        status: 'error',
        duration: 1000,
        position: 'top',
      });
      navigate(-1);
    }
  }, [dataStatus]);

  return (
    <PageContainer
      hideHeader
      hideNavBar
      afterHeaderComponent={
        <>
          <BackwardBar title="返回" onNavigate={() => navigate(-1)} />
          <LocationBar />
          <Box px={4} borderBottomWidth="2px" borderColor="primary" bgColor="white">
            <Content py={2}>
              <Heading fontSize="md">取餐地點詳情頁</Heading>
            </Content>
          </Box>
        </>
      }
    >
      {fp.eq(dataStatus, FirebaseDataStatus.INIT) && (
        <Center my={4}>
          <Spinner size="lg" />
        </Center>
      )}

      {fp.eq(dataStatus, FirebaseDataStatus.EXIST) && (
        <>
          <Box>
            <Content>
              <AspectRatio ratio={3 / 2}>
                <Image src={remotePickMealPoint.image || tzuchiDefaultImg} />
              </AspectRatio>
            </Content>
          </Box>

          <Box px={4}>
            <Content py={5}>
              <VStack align="stretch" spacing={4} divider={<StackDivider borderColor="gray.400" />}>
                {/** location base info */}
                <VStack align="flex-start" spacing={2}>
                  <Heading size="sm">{remotePickMealPoint.name}</Heading>
                  <HStack>
                    <Icon as={IoCallOutline} boxSize="1.25rem" color="gray.500" />
                    <Text fontSize="sm">{remotePickMealPoint.phoneNumber}</Text>
                  </HStack>
                  <HStack>
                    <Icon as={IoLocationOutline} boxSize="1.25rem" color="gray.500" />
                    <Text fontSize="sm">{remotePickMealPoint.location.address}</Text>
                  </HStack>
                  <Link
                    borderWidth="1px"
                    borderRadius="5px"
                    borderColor="primary"
                    p={1}
                    _hover={{
                      textDecoration: 'none',
                    }}
                    href={`https://maps.google.com/?q=${remotePickMealPoint.location.geoPoint.latitude},${remotePickMealPoint.location.geoPoint.longitude}`}
                    isExternal
                  >
                    <HStack spacing={1}>
                      <Icon as={IoLocationOutline} boxSize="1.25rem" color="primary" />
                      <Text fontSize="xs" color="primary">
                        查看地圖
                      </Text>
                    </HStack>
                  </Link>
                </VStack>

                {/** location description */}
                <VStack align="flex-start" spacing={2}>
                  <SectionLabel title="位置描述" icon={AiOutlineShop} />
                  <Text fontSize="sm">{remotePickMealPoint.description}</Text>
                </VStack>

                {/** location operating time list  */}
                <VStack align="flex-start" spacing={2}>
                  <SectionLabel title="營業時間" icon={IoTimeOutline} />
                  <OperatingTimeList operatingTime={remotePickMealPoint.operatingTime} />
                </VStack>
              </VStack>
            </Content>
          </Box>
        </>
      )}
    </PageContainer>
  );
}
